import { computed, onBeforeUnmount, watch } from 'vue'
import { useAuthStore } from '~/stores/useAuthStore'
import { useSiteModules } from '~/composables/useSiteModules'
import { isDashboardPathAllowed } from '~/utils/practice/roles'
import { useModulePurchasePrompt } from '~/composables/useModulePurchasePrompt'

export interface DashboardNavItem {
  label: string
  icon: string
  to?: string
  disabled?: boolean
  active?: boolean
  badge?: string | number
  onSelect?: () => void
}

const VIRTUAL_VISITS_PATH = '/dashboard/booking/virtual-visits'

const NAV_DEFS: Array<{ label: string; icon: string; to: string; requiresSub?: boolean; module?: string; modules?: string[] }> = [
  { label: 'نمای کلی', icon: 'i-lucide-layout-dashboard', to: '/dashboard', requiresSub: true },
  { label: 'لیست بیماران', icon: 'i-lucide-clipboard-check', to: '/dashboard/reception', requiresSub: true, modules: ['onlineBooking', 'healthRecord'] },
  { label: 'خدمات و نوبت دهی', icon: 'i-lucide-calendar-days', to: '/dashboard/booking', requiresSub: true, module: 'onlineBooking' },
  { label: 'سوابق سلامت', icon: 'i-lucide-folder-heart', to: '/dashboard/health', requiresSub: true, module: 'healthRecord' },
  { label: 'بلاگ و مقالات', icon: 'i-lucide-newspaper', to: '/dashboard/blog', requiresSub: true, module: 'blog' },
  { label: 'کمپین فروش', icon: 'i-lucide-megaphone', to: '/dashboard/campaign', requiresSub: true, module: 'clinicCampaign' },
  { label: 'جمع‌آوری مشتری', icon: 'i-lucide-magnet', to: '/dashboard/leads', requiresSub: true, module: 'leadGen' },
]

const BREADCRUMB_LABELS: Record<string, string> = {
  '/dashboard': 'نمای کلی',
  '/dashboard/booking': 'خدمات و نوبت دهی',
  [VIRTUAL_VISITS_PATH]: 'ویزیت مجازی',
  '/dashboard/health': 'سوابق سلامت',
  '/dashboard/health/forms': 'فرم‌های شرح حال',
  '/dashboard/reception': 'لیست بیماران',
  '/dashboard/blog': 'بلاگ و مقالات',
  '/dashboard/blog/new': 'مقالهٔ جدید',
  '/dashboard/blog/categories': 'دسته‌ها',
  '/dashboard/blog/comments': 'دیدگاه‌ها',
  '/dashboard/blog/settings': 'تنظیمات بلاگ',
  '/dashboard/services': 'خدمات',
  '/dashboard/services/packages': 'پکیج‌ها',
  '/dashboard/services/packages/purchases': 'خریدها',
  '/dashboard/campaign': 'کمپین فروش',
  '/dashboard/campaign/coupons': 'کوپن‌ها',
  '/dashboard/campaign/sms': 'پیامک هدفمند',
  '/dashboard/campaign/spin': 'گردونه شانس',
  '/dashboard/campaign/analytics': 'آمار کمپین',
  '/dashboard/campaign/flash-sales': 'فروش ویژه',
  '/dashboard/campaign/subscribers': 'مشترکان',
  '/dashboard/campaign/occasions': 'مناسبت‌ها',
  '/dashboard/campaign/gift-cards': 'کارت هدیه',
  '/dashboard/campaign/landings': 'لندینگ کمپین',
  '/dashboard/campaign/settings': 'تنظیمات کمپین',
  '/dashboard/leads': 'جمع‌آوری مشتری',
  '/dashboard/leads/inbox': 'صندوق لید',
  '/dashboard/leads/magnets': 'محتوای رایگان',
  '/dashboard/leads/calculators': 'ماشین‌حساب‌ها',
  '/dashboard/leads/quizzes': 'آزمون‌ها',
  '/dashboard/leads/surveys': 'نظرسنجی‌ها',
  '/dashboard/leads/guides': 'راهنمای شخصی',
  '/dashboard/leads/webinars': 'وبینار و رویداد',
  '/dashboard/leads/waitlists': 'لیست انتظار',
  '/dashboard/leads/chat': 'چت اولیه',
  '/dashboard/leads/ambassadors': 'سفیران',
  '/dashboard/leads/analytics': 'آمار لید',
  '/dashboard/leads/settings': 'تنظیمات لید',
  '/dashboard/booking/services': 'خدمات قابل رزرو',
  '/dashboard/booking/doctors': 'پزشکان',
  '/dashboard/booking/schedule': 'برنامه کاری',
  '/dashboard/booking/settings': 'تنظیمات نوبت‌دهی',
  '/dashboard/tickets': 'تیکت‌های پشتیبانی',
  '/dashboard/subscription': 'اشتراک',
  '/dashboard/domain': 'دامنه',
  '/dashboard/settings': 'تنظیمات',
}

function isVirtualVisitsPath(path: string) {
  return path === VIRTUAL_VISITS_PATH || path.startsWith(`${VIRTUAL_VISITS_PATH}/`)
}

function isNavActive(to: string, path: string) {
  if (to === '/dashboard') return path === to
  if (to === '/dashboard/booking') {
    return path === to || (path.startsWith(`${to}/`) && !isVirtualVisitsPath(path))
  }
  if (to === '/dashboard/blog') {
    return path === to || path.startsWith(`${to}/`)
  }
  return path === to || path.startsWith(`${to}/`)
}

export function useDashboardNav() {
  const auth = useAuthStore()
  const route = useRoute()
  const { enabledKeys } = useSiteModules()
  const {
    data: catalog,
    error: catalogError,
    refresh: refreshCatalog,
  } = useFetch<{ modules: Array<{ key: string; name: { fa?: string }; businessTypes: string[] }> }>('/api/plans')
  const { openModulePurchase } = useModulePurchasePrompt()
  const router = useRouter()

  const toast = useToast()
  const locked = computed(() => !auth.hasActiveSubscription)
  let catalogRetryCount = 0
  let catalogRetryTimer: ReturnType<typeof setTimeout> | undefined
  let catalogFailureNotified = false

  watch([catalogError, catalog], ([error, data]) => {
    if (!import.meta.client) return

    if (!error && data?.modules.length) {
      catalogRetryCount = 0
      catalogFailureNotified = false
      return
    }

    if (!error && !data) return

    if (catalogRetryCount < 2) {
      catalogRetryCount += 1
      catalogRetryTimer = setTimeout(() => {
        void refreshCatalog()
      }, catalogRetryCount * 1000)
      return
    }

    if (!catalogFailureNotified) {
      catalogFailureNotified = true
      toast.add({
        title: 'بارگذاری منو کامل نشد',
        description: 'فهرست امکانات موقتاً در دسترس نیست. کمی بعد دوباره تلاش کنید.',
        color: 'error',
      })
    }
  }, { immediate: true })

  onBeforeUnmount(() => {
    if (catalogRetryTimer) clearTimeout(catalogRetryTimer)
  })

  const catalogUnavailable = computed(() => !catalog.value?.modules.length)

  function explainLocked() {
    toast.add({
      title: 'اشتراک فعال نیست',
      description: 'برای استفاده از این بخش اشتراک را تمدید کنید.',
      color: 'warning',
    })
  }
  function explainModuleLocked(key: string, title: string) {
    if (!auth.hasActiveSubscription) {
      void router.push('/dashboard/subscription')
      return
    }
    openModulePurchase(key, title)
  }

  function catalogModule(key: string) {
    return catalog.value?.modules.find(module => module.key === key
      && (!module.businessTypes.length || module.businessTypes.includes(auth.site?.businessType ?? 'solo')))
  }

  const role = computed(() => auth.user?.siteRole ?? 'owner')
  const practiceLabel = computed(() => auth.site?.businessType === 'clinic' ? 'تنظیمات کلینیک' : 'تنظیمات مطب')

  const visibleDefs = computed(() => {
    const defs = [...NAV_DEFS]
    if (role.value === 'owner') {
      defs.push({
        label: practiceLabel.value,
        icon: 'i-lucide-building-2',
        to: '/dashboard/practice',
        requiresSub: true,
      })
    }
    else {
      defs.push({
        label: 'تنظیمات',
        icon: 'i-lucide-settings',
        to: '/dashboard/settings',
        requiresSub: true,
      })
    }
    return defs.filter((item) => {
      if (!isDashboardPathAllowed(role.value, item.to, auth.user?.permissions)) return false
      if (catalogUnavailable.value) return true
      const modules = 'modules' in item ? item.modules : undefined
      const moduleKey = 'module' in item ? item.module : undefined
      if (modules?.length) return modules.some(key => !!catalogModule(key))
      return !moduleKey || !!catalogModule(moduleKey)
    })
  })

  const navItems = computed((): DashboardNavItem[][] => {
    const path = route.path.replace(/\/$/, '') || '/dashboard'
    const items = visibleDefs.value.map((item) => {
      const itemModules = 'modules' in item ? item.modules : undefined
      const moduleKey = 'module' in item ? item.module : undefined
      const missingModule = itemModules?.find(key => catalogModule(key) && !enabledKeys.value.has(key))
        ?? (moduleKey && !enabledKeys.value.has(moduleKey) ? moduleKey : undefined)
      const module = missingModule ? catalogModule(missingModule) : undefined
      const moduleCatalogUnavailable = catalogUnavailable.value && !!(itemModules?.length || moduleKey)
      const moduleLocked = itemModules?.length
        ? !itemModules.some(key => enabledKeys.value.has(key)) && !!missingModule && !!module
        : !!missingModule && !!module
      const isLocked = (locked.value && !!item.requiresSub) || moduleLocked || moduleCatalogUnavailable
      const title = module?.name.fa || item.label
      return {
        label: item.label,
        icon: isLocked ? 'i-lucide-lock' : item.icon,
        to: isLocked ? undefined : item.to,
        onSelect: isLocked
          ? moduleCatalogUnavailable && !locked.value
            ? () => {
                toast.add({
                  title: 'فهرست امکانات در حال بارگذاری است',
                  description: 'پس از بارگذاری فهرست، دوباره این بخش را انتخاب کنید.',
                  color: 'warning',
                })
                void refreshCatalog()
              }
            : moduleLocked
            ? () => explainModuleLocked(missingModule!, title)
            : explainLocked
          : undefined,
        active: !isLocked && isNavActive(item.to, path),
      }
    })
    return [items]
  })

  const searchGroups = computed(() => [
    {
      id: 'pages',
      label: 'صفحات',
      items: visibleDefs.value.filter((d) => {
        const itemModules = 'modules' in d ? d.modules : undefined
        const moduleKey = 'module' in d ? d.module : undefined
        if (catalogUnavailable.value && (itemModules?.length || moduleKey)) return false
        const allMissing = itemModules?.length
          ? !itemModules.some(key => enabledKeys.value.has(key))
            && itemModules.some(key => catalogModule(key) && !enabledKeys.value.has(key))
          : !!(moduleKey && catalogModule(moduleKey) && !enabledKeys.value.has(moduleKey))
        return !(locked.value && d.requiresSub)
          && !allMissing
      }).map((d) => ({
        id: d.to,
        label: d.label,
        icon: d.icon,
        to: d.to,
      })),
    },
  ])

  const breadcrumbs = computed(() => {
    const path = route.path.replace(/\/$/, '') || '/dashboard'
    const home = locked.value ? '/dashboard/subscription' : '/dashboard'
    if (path.startsWith(`${VIRTUAL_VISITS_PATH}/`)) {
      return [
        { label: 'داشبورد', to: home },
        { label: 'ویزیت مجازی', to: VIRTUAL_VISITS_PATH },
        { label: 'اتاق ویزیت' },
      ]
    }
    if (path === VIRTUAL_VISITS_PATH) {
      return [
        { label: 'داشبورد', to: home },
        { label: 'ویزیت مجازی' },
      ]
    }
    if (path.startsWith('/dashboard/booking/') && path !== '/dashboard/booking') {
      return [
        { label: 'داشبورد', to: home },
        { label: 'خدمات و نوبت دهی', to: '/dashboard/booking' },
        { label: BREADCRUMB_LABELS[path] ?? 'جزئیات' },
      ]
    }
    if (path.startsWith('/dashboard/health/forms/')) {
      return [
        { label: 'داشبورد', to: home },
        { label: 'سوابق سلامت', to: '/dashboard/health' },
        { label: 'فرم‌ها', to: '/dashboard/health/forms' },
        { label: 'فرم‌ساز' },
      ]
    }
    if (path.startsWith('/dashboard/health/entries/')) {
      return [
        { label: 'داشبورد', to: home },
        { label: 'سوابق سلامت', to: '/dashboard/health' },
        { label: 'شرح حال' },
      ]
    }
    if (path.startsWith('/dashboard/health/') && path !== '/dashboard/health' && path !== '/dashboard/health/forms') {
      return [
        { label: 'داشبورد', to: home },
        { label: 'سوابق سلامت', to: '/dashboard/health' },
        { label: 'جزئیات سابقه' },
      ]
    }
    if (path.startsWith('/dashboard/blog/') && path !== '/dashboard/blog') {
      return [
        { label: 'داشبورد', to: home },
        { label: 'بلاگ و مقالات', to: '/dashboard/blog' },
        { label: BREADCRUMB_LABELS[path] ?? (path.includes('/blog/') && path.split('/').length > 3 ? 'ویرایش مقاله' : 'جزئیات') },
      ]
    }
    if (path.startsWith('/dashboard/campaign/') && path !== '/dashboard/campaign') {
      return [
        { label: 'داشبورد', to: home },
        { label: 'کمپین فروش', to: '/dashboard/campaign' },
        { label: BREADCRUMB_LABELS[path] ?? 'جزئیات' },
      ]
    }
    if (path.startsWith('/dashboard/leads/') && path !== '/dashboard/leads') {
      return [
        { label: 'داشبورد', to: home },
        { label: 'جمع‌آوری مشتری', to: '/dashboard/leads' },
        { label: BREADCRUMB_LABELS[path] ?? 'جزئیات' },
      ]
    }
    if (path.startsWith('/dashboard/reception/') && path !== '/dashboard/reception') {
      return [
        { label: 'داشبورد', to: home },
        { label: 'لیست بیماران', to: '/dashboard/reception' },
        { label: 'بیمار' },
      ]
    }
    if (path.startsWith('/dashboard/tickets/') && path !== '/dashboard/tickets') {
      return [
        { label: 'داشبورد', to: home },
        { label: 'تیکت‌های پشتیبانی', to: '/dashboard/tickets' },
        { label: 'جزئیات تیکت' },
      ]
    }
    if (path === '/dashboard/practice') {
      return [
        { label: 'داشبورد', to: home },
        { label: practiceLabel.value },
      ]
    }
    const label = BREADCRUMB_LABELS[path] ?? 'داشبورد'
    return [
      { label: 'داشبورد', to: home },
      { label },
    ]
  })

  const pageTitle = computed(() => {
    const path = route.path.replace(/\/$/, '') || '/dashboard'
    if (isVirtualVisitsPath(path)) {
      return path === VIRTUAL_VISITS_PATH ? 'ویزیت مجازی' : 'اتاق ویزیت'
    }
    if (path.startsWith('/dashboard/booking/') && path !== '/dashboard/booking') {
      return BREADCRUMB_LABELS[path] ?? 'خدمات و نوبت دهی'
    }
    if (path.startsWith('/dashboard/health/forms/')) return 'فرم‌ساز'
    if (path.startsWith('/dashboard/health/entries/')) return 'شرح حال'
    if (path.startsWith('/dashboard/health/print/')) return 'چاپ سابقه'
    if (path.startsWith('/dashboard/health/') && path !== '/dashboard/health' && path !== '/dashboard/health/forms') return 'سابقه بیمار'
    if (path.startsWith('/dashboard/blog/comments')) return 'دیدگاه‌ها'
    if (path === '/dashboard/blog/new' || /^\/dashboard\/blog\/[^/]+$/.test(path)) return path === '/dashboard/blog/new' ? 'مقالهٔ جدید' : 'ویرایش مقاله'
    if (path.startsWith('/dashboard/blog/')) return BREADCRUMB_LABELS[path] ?? 'بلاگ'
    if (path.startsWith('/dashboard/services/')) {
      if (path.includes('/edit/')) return 'ویرایش پکیج'
      return BREADCRUMB_LABELS[path] ?? 'خدمات'
    }
    if (path.startsWith('/dashboard/campaign/')) return BREADCRUMB_LABELS[path] ?? 'کمپین فروش'
    if (path.startsWith('/dashboard/leads/')) return BREADCRUMB_LABELS[path] ?? 'جمع‌آوری مشتری'
    if (path.startsWith('/dashboard/reception/') && path !== '/dashboard/reception') return 'پذیرش بیمار'
    if (path.startsWith('/dashboard/tickets/') && path !== '/dashboard/tickets') return 'جزئیات تیکت'
    if (path === '/dashboard/practice') return practiceLabel.value
    return BREADCRUMB_LABELS[path] ?? 'داشبورد'
  })

  return {
    locked,
    navItems,
    searchGroups,
    breadcrumbs,
    pageTitle,
  }
}
